import { useState, type FormEvent } from 'react';
import { BORNES_BULLE } from '@nextilt/commun';
import type { MaillonDeBoucle } from '../../partage/api/types';
import { conditionsPosees, type ConditionDeLaBoucle } from './ceQuiLaRetient';

type Issue = 'retirer' | 'inserer';

/** Quand la boucle ne passe que par l'arbre : aucune condition posée à retirer ni à remplacer. */
const RIEN_A_RETIRER = 'La boucle ne passe que par des bulles rangées dedans : rien à retirer ici';

/**
 * Les trois issues d'une boucle (SPEC §3, docs/adr/0004).
 *
 * Une boucle se casse de l'extérieur, et toujours sur une condition : on renonce au lien
 * qu'on posait, on retire une condition déjà posée qui n'en était pas une, ou on la remplace
 * par la bulle qu'on paye pour sortir. Les deux dernières agissent sur un lien DÉJÀ posé :
 * on demande lequel, en le nommant, avant de toucher à quoi que ce soit.
 *
 * Seules les conditions posées à la main se proposent. Une condition verticale — la bulle
 * est dedans — ne se retire pas d'un clic ici : c'est l'arbre, pas un lien.
 *
 * Le composant ne fait aucun appel : il dit ce qu'on a choisi, la fiche fait le reste.
 */
export function IssuesDeLaBoucle({
  tour,
  enCours,
  echec,
  onNePasPoser,
  onRetirer,
  onInserer,
}: {
  /** Les maillons dans l'ordre du dessin : chacun attend le suivant. */
  tour: MaillonDeBoucle[];
  enCours: boolean;
  echec: boolean;
  onNePasPoser: () => void;
  onRetirer: (lienId: string) => void;
  onInserer: (lienId: string, titre: string) => void;
}) {
  const conditions = conditionsPosees(tour);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [choisie, setChoisie] = useState<string | null>(
    conditions.length === 1 ? (conditions[0]?.lienId ?? null) : null
  );
  const [titre, setTitre] = useState('');

  const aucune = conditions.length === 0;
  const remplacee = conditions.find((c) => c.lienId === choisie) ?? null;

  function inserer(evenement: FormEvent) {
    evenement.preventDefault();
    const propre = titre.trim();
    if (!remplacee || !propre || enCours) return;
    onInserer(remplacee.lienId, propre);
  }

  return (
    <>
      <div className="cycle-issues">
        <article>
          <h3>Retirer un maillon</h3>
          <p>
            Était-ce <em>vraiment</em> une condition&nbsp;? Beaucoup d’agences acceptent le RIB
            de l’ancienne banque.
          </p>
          <button type="button" className="cycle-geste" onClick={onNePasPoser}>
            Ne pas poser ce lien
          </button>
        </article>

        <article className={issue === 'retirer' ? 'est-choisie' : undefined}>
          <h3>Ce n’en était pas une</h3>
          <p>
            C’était une <em>cause</em>, pas une condition. Ça t’arrangeait&nbsp;; ça ne
            t’empêchait pas de commencer.
          </p>
          <button
            type="button"
            className="cycle-geste"
            disabled={aucune}
            title={aucune ? RIEN_A_RETIRER : undefined}
            aria-expanded={issue === 'retirer'}
            onClick={() => setIssue(issue === 'retirer' ? null : 'retirer')}
          >
            Retirer l’autre condition
          </button>
        </article>

        <article className={`est-la-vraie${issue === 'inserer' ? ' est-choisie' : ''}`}>
          <h3>Insérer un maillon</h3>
          <p>
            La bulle qu’on ajoute pour sortir — une attestation d’hébergement donne un
            justificatif sans bail. C’est le maillon qu’on paye pour sortir.
          </p>
          <button
            type="button"
            className="cycle-geste est-ambre"
            disabled={aucune}
            title={aucune ? RIEN_A_RETIRER : undefined}
            aria-expanded={issue === 'inserer'}
            onClick={() => setIssue(issue === 'inserer' ? null : 'inserer')}
          >
            Ajouter cette bulle ici
          </button>
        </article>
      </div>

      {issue === 'retirer' && (
        <div className="cycle-suite">
          <p className="cycle-suite-question">
            {conditions.length > 1
              ? 'Laquelle n’en était pas une ?'
              : 'Celle-ci n’en était pas une :'}
          </p>
          <ul className="cycle-suite-liste">
            {conditions.map((c) => (
              <li key={c.lienId}>
                <PhraseDeCondition condition={c} />
                <button
                  type="button"
                  className="bouton bouton-discret"
                  disabled={enCours}
                  onClick={() => onRetirer(c.lienId)}
                >
                  La retirer, et poser mon lien
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {issue === 'inserer' && (
        <form className="cycle-suite est-ambree" onSubmit={inserer}>
          {conditions.length > 1 && (
            <>
              <p className="cycle-suite-question">Quelle condition remplace-t-elle&nbsp;?</p>
              <ul className="cycle-suite-liste">
                {conditions.map((c) => (
                  <li key={c.lienId}>
                    <button
                      type="button"
                      className={`cycle-suite-choix${c.lienId === choisie ? ' est-choisi' : ''}`}
                      aria-pressed={c.lienId === choisie}
                      onClick={() => setChoisie(c.lienId)}
                    >
                      <PhraseDeCondition condition={c} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {remplacee && (
            <>
              <p className="cycle-suite-question">
                Elle se range dans «&nbsp;{remplacee.attend.titre}&nbsp;», qui n’attendra plus
                «&nbsp;{remplacee.attendu.titre}&nbsp;».
              </p>
              <div className="cycle-suite-saisie">
                <input
                  className="champ"
                  value={titre}
                  onChange={(e) => setTitre(e.target.value)}
                  placeholder="La bulle qui fait sortir, par exemple une attestation"
                  aria-label="la bulle qu’on ajoute pour sortir"
                  maxLength={BORNES_BULLE.titreMax}
                  autoFocus
                />
                <button
                  type="submit"
                  className="bouton bouton-discret"
                  disabled={!titre.trim() || enCours}
                >
                  Ajouter, et poser mon lien
                </button>
              </div>
            </>
          )}
        </form>
      )}

      {echec && (
        <p className="cycle-suite-echec" role="status">
          Ça n’est pas passé. Réessaie dans un instant.
        </p>
      )}
    </>
  );
}

/** « Ouvrir le compte attend Justificatif » : les deux titres, dans le sens de la condition. */
function PhraseDeCondition({ condition }: { condition: ConditionDeLaBoucle }) {
  return (
    <span className="cycle-suite-phrase">
      <strong>{condition.attend.titre}</strong> attend <strong>{condition.attendu.titre}</strong>
    </span>
  );
}
